import { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Dropdown from '@/components/General/Dropdown';
import PageLoading from '@/components/General/PageLoading';
import useCountries from '@/hooks/countries';
import useDobInput from '@/hooks/dobInputHook';
import useInput from '@/hooks/inputHook';
import useRequiredInput from '@/hooks/requiredInputHook';
import { getDateOptions, getMonthOptions, getYearOptions } from '@/lib/date';
import * as gtag from '@/lib/gtag';
import eventAdd from '@/lib/eventAdd';
import MSConnections from '@/lib/msconnections';
import MSTypes, { MSTypesEnum } from '@/lib/mstypes';
import MSReasonsForNoDmt, {
  MSReasonsForNoDmtTypes,
  MSReasonsforNoDmtOptions,
} from '@/lib/msReasonsForNoDMT';
import MSWhoInfluencedDmtDecisionList, {
  msWhoInfluencedDmtDecision,
} from '@/lib/msWhoInfluencedDmtDecisionList';
import MSImportantFactorsInDmtDecision, {
  MSConcernType,
} from '@/lib/msImportantFactorsInDmtDecision';
import { getTreatmentOptions, getTreatmentType } from '@/lib/treatments';
import getYearDiagnosedOptions from '@/lib/yearDiagnosed';
import { useAuth } from '@/providers/AuthProvider';
import { functions } from '@/firebase/firebase';
import RankPreference from '@/components/General/RankPreference';
import Score from '@/components/General/Score';
import { countriesWithRegions, countiesOfUK } from '@/lib/regions';
import {
  IUser,
  MSConnection,
  YesNoRadioType,
  Region,
} from '@/types/Collections/users';
import MSImportantActivities from '@/lib/msImportantActivities';
import { updateProfile } from '@/components/Profile/api';
import { updateSettings } from '@/providers/AuthProvider/hooks/useSettings';

const WelcomePage: NextPage = () => {
  const { authLoaded, isLoggedIn, uid, settings, auth, isVerified } = useAuth();
  const router = useRouter();

  const { countriesList } = useCountries();

  const {
    value: content,
    bind: bindContent,
    reset: resetContent,
  } = useInput('');

  const [renderOtherTreatmentField, setOtherTreatmentField] =
    useState<boolean>(false);
  const [monthDiagnosed, setMonthDiagnosed] = useState<number | ''>('');
  const [monthDmtStarted, setMonthDmtStarted] = useState<number | ''>('');

  const [msConnection, setMsConnection] = useState<MSConnection | ''>('');
  const [msConnectionValid, setMsConnectionValid] = useState<boolean>(false);

  const [isTakingDMT, setIsTakingDMT] = useState<YesNoRadioType | ''>('');
  const [isTakingDMTValid, setIsTakingDMTValid] = useState<boolean>(false);

  const [isOnDesiredDMT, setIsOnDesiredDMT] = useState<YesNoRadioType | ''>('');
  const [isOnDesiredDMTValid, setIsOnDesiredDMTValid] =
    useState<boolean>(false);

  const [yearDmtStarted, setYearDmtStarted] = useState<number | ''>('');
  const [dmtStartDateValid, setDmtStartDateValid] = useState<boolean>(false);
  const [dmtStartDateErrorMessage, setDmtStartDateErrorMessage] =
    useState<string>('');

  const [touched, setTouched] = useState({
    genderOption: false,
    country: false,
    msConnection: false,
    treatment: false,
    dataPermission: false,
    diagnosisDate: false,
    researchOpt: false,
    msType: false,
    isTakingDMT: false,
    reasonForNotUsingDMT: false,
    dmtStartDate: false,
    isOnDesiredDMT: false,
    dmtInfluentialPerson: false,
    dmtRecommendationScore: false,
    importantFactorInDmtDecision: false,
    msImportantActivitiesList: false,
    region: false,
    county: false,
    hcpReferral: false,
  });

  const {
    value: dob,
    stringValue: dobString,
    bindYear: bindDobYear,
    bindMonth: bindDobMonth,
    bindDay: bindDobDay,
    valid: dobValid,
    touched: dobTouched,
  } = useDobInput();
  const [genderOption, setGenderOption] = useState<string>('');
  const [genderOptionValid, setGenderOptionValid] = useState<boolean>(false);
  const { value: gender, bind: bindGender } = useInput('');

  const [msType, setMsType] = useState<MSTypesEnum | ''>('');
  const [msTypeValid, setMsTypeValid] = useState<boolean>(false);

  const [dmtInfluentialPerson, setDmtInfluentialPerson] = useState<
    msWhoInfluencedDmtDecision | ''
  >('');
  const [dmtInfluentialPersonValid, setDmtInfluentialPersonValid] =
    useState<boolean>(false);

  const [dmtRecommendationScore, setDmtRecommendationScore] = useState<
    number | ''
  >('');
  const [dmtRecommendationScoreValid, setDmtRecommendationScoreValid] =
    useState<boolean>(false);

  const [reasonForNotUsingDMT, setReasonForNotUsingDMT] = useState<
    MSReasonsForNoDmtTypes | ''
  >('');
  const [reasonForNotUsingDMTValid, setReasonForNotUsingDMTValid] =
    useState<boolean>(false);

  const [importantFactorInDmtDecision, setImportantFactorInDmtDecision] =
    useState<MSConcernType | ''>('');
  const [
    importantFactorInDmtDecisionValid,
    setImportantFactorInDmtDecisionValid,
  ] = useState<boolean>(false);

  const [treatment, setTreatment] = useState<string>('');
  const [treatmentValid, setTreatmentValid] = useState<boolean>(false);
  const [yearDiagnosed, setYearDiagnosed] = useState<number | ''>('');
  const [diagnosisDateValid, setDiagnosisDateValid] = useState<boolean>(false);
  const [diagnosisDateErrorMessage, setDiagnosisDateErrorMessage] =
    useState<string>('');
  const {
    value: country,
    setValue: setCountry,
    valid: countryValid,
    setValid: setCountryValid,
    isNotEmpty: countryIsNotEmpty,
  } = useRequiredInput('');

  const { value: countryName, setValue: setCountryName } = useInput('');

  const [region, setRegion] = useState<Region | ''>('');
  const [county, setCounty] = useState<string>('');

  const [researchOptIn, setResearchOptIn] = useState<string | boolean>('');
  const [researchOptInValid, setResearchOptInValid] = useState(
    researchOptIn !== '' || false,
  );

  const [dataPermission, setDataPermission] = useState<string | boolean>('');

  const [hcpReferral, setHcpReferral] = useState<'' | YesNoRadioType>('');
  const [hcpReferralValid, setHcpReferralValid] = useState(
    hcpReferral !== '' || false,
  );
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [showGenderOtherField, setShowGenderOtherField] =
    useState<boolean>(false);
  const [showMSFields, setShowMSFields] = useState<boolean>(false);

  const [msImportantActivitiesList, setMsImportantActivitiesList] = useState(
    MSImportantActivities,
  );

  const isMsImportantActivitiesListValid = useMemo(() => {
    const filteredList = msImportantActivitiesList?.filter(
      item => item.rank === undefined,
    );

    if (
      msConnection === MSConnection['I have MS'] &&
      isTakingDMT === YesNoRadioType.yes
    ) {
      return filteredList?.length === 0;
    }

    return true;
  }, [isTakingDMT, msConnection, msImportantActivitiesList]);

  const isRegionValid = useMemo(() => {
    const validCountries = ['GB', 'US', 'CA', 'AU'];
    if (validCountries.includes(country) && region === '') {
      return false;
    }
    return true;
  }, [country, region]);

  const isCountyValid = useMemo(() => {
    if (country === 'GB' && region !== '' && county === '') {
      return false;
    }

    return true;
  }, [country, county, region]);

  const handleResearchOptIn = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResearchOptIn(event.currentTarget.value === 'yes');
    setResearchOptInValid(event.currentTarget.value !== '');
  };

  const handleDataPermission = () => {
    setDataPermission(!dataPermission);
  };

  const save = async (profile: Partial<IUser>) => {
    try {
      if (uid) {
        await updateProfile(uid, { ...profile, isActivated: true });

        const theDate = serverTimestamp();
        const newSettings = {
          researchOptIn,
          needsOnboarding: false,
          dataPermission: theDate,
        };
        await updateSettings(uid, newSettings);

        const followingDefaultsFunction = httpsCallable(
          functions,
          'followCallable-followingDefaults',
        );
        await followingDefaultsFunction({ uid });

        gtag.event({
          action: 'profile.completed',
          params: {
            name: 'Completed Profile',
            uid,
            connection: profile.msConnection,
          },
        });
      }

      if (uid && auth?.email) {
        eventAdd({
          uid,
          eventType: 'PROFILE:COMPLETED',
          eventSource: 'PROFILE',
          eventSourceId: uid,
          meta: {},
          email: auth.email,
          platform: 'web',
        });
      }

      // Redirect
      if (!isVerified) {
        router.push('/verify');
      } else {
        router.push('/home');
      }
    } catch (e) {
      // console.error('Error: Save not executed successfully.');
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !dobValid ||
      !genderOptionValid ||
      !countryValid ||
      !msConnectionValid ||
      !msTypeValid ||
      !treatmentValid ||
      !diagnosisDateValid ||
      !researchOptInValid ||
      !dataPermission ||
      !isRegionValid ||
      !isCountyValid ||
      !isMsImportantActivitiesListValid ||
      !dmtRecommendationScoreValid ||
      !reasonForNotUsingDMTValid ||
      !dmtInfluentialPersonValid
    ) {
      return false;
    }
    setIsSubmitted(true);
    const profile: Partial<IUser> = {
      msConnection,
      msType: '',
      treatment: '',
      genderOption,
      gender,
      yearDiagnosed: '',
      monthDiagnosed: '',
      dob,
      dobString,
      country,
      countryName,
      region,
      isUserActivitiesPopulated: true,
      isTakingDMT: '',
      reasonForNotUsingDMT: '',
      monthDmtStarted: '',
      yearDmtStarted: '',
      isOnDesiredDMT: '',
      dmtInfluentialPerson: '',
      msImportantActivitiesList: [],
      importantFactorInDmtDecision: '',
      county: '',
      hcpReferral: '',
    };
    if (country === 'GB') {
      profile.county = county;
    }

    if (profile.msConnection === MSConnection['I have MS']) {
      profile.msType = msType;
      profile.yearDiagnosed = yearDiagnosed;
      if (monthDiagnosed !== '') {
        profile.monthDiagnosed = Number(monthDiagnosed) + 1;
      } else {
        profile.monthDiagnosed = '';
      }

      profile.hcpReferral = hcpReferral;

      if (isTakingDMT === 'yes') {
        profile.msImportantActivitiesList = msImportantActivitiesList;
        profile.isTakingDMT = isTakingDMT;
        if (monthDmtStarted !== '') {
          profile.monthDmtStarted = Number(monthDmtStarted) + 1;
        } else {
          profile.monthDmtStarted = '';
        }
        profile.yearDmtStarted = yearDmtStarted;
        profile.isOnDesiredDMT = isOnDesiredDMT;
        profile.dmtInfluentialPerson = dmtInfluentialPerson;
        profile.dmtRecommendationScore = dmtRecommendationScore;
        profile.treatment = renderOtherTreatmentField ? content : treatment;
        profile.treatmentType = getTreatmentType(treatment);
        profile.importantFactorInDmtDecision = importantFactorInDmtDecision;
        if (treatment !== '') {
          profile.treatmentType = getTreatmentType(treatment);
        }
      } else if (isTakingDMT === 'no') {
        profile.isTakingDMT = isTakingDMT;
        profile.reasonForNotUsingDMT = reasonForNotUsingDMT;
        profile.dmtInfluentialPerson = dmtInfluentialPerson;
      }
    }
    if (profile.genderOption === 'Male' || profile.genderOption === 'Female') {
      profile.gender = profile.genderOption;
    }

    return save(profile);
  };

  const onDmtUsageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'yes') {
      setTreatmentValid(treatment !== '');
      setDmtStartDateValid(yearDmtStarted !== '');
      setDmtInfluentialPersonValid(dmtInfluentialPerson !== '');
      setDmtRecommendationScoreValid(!!dmtInfluentialPerson);
      setIsOnDesiredDMTValid(isOnDesiredDMT !== '');
      setImportantFactorInDmtDecisionValid(importantFactorInDmtDecision !== '');
      setReasonForNotUsingDMTValid(true);
    }
    if (event.target.value === 'no') {
      setReasonForNotUsingDMTValid(reasonForNotUsingDMT !== '');
      setDmtInfluentialPersonValid(dmtInfluentialPerson !== '');
      setIsOnDesiredDMTValid(true);
      setDmtStartDateValid(true);
      setTreatmentValid(true);
      setDmtInfluentialPersonValid(true);
      setDmtRecommendationScoreValid(true);
      setIsOnDesiredDMTValid(true);
      setImportantFactorInDmtDecisionValid(true);
    }
    setIsTakingDMT(event.target.value as YesNoRadioType);
    setIsTakingDMTValid(event.target.value !== '');
  };

  useEffect(() => {
    if (yearDiagnosed !== '' && monthDiagnosed !== '') {
      if (
        Number(yearDiagnosed) >= new Date().getFullYear() &&
        Number(monthDiagnosed) > new Date().getMonth()
      ) {
        setDiagnosisDateErrorMessage(
          "Please don't select a date in the future.",
        );
        setDiagnosisDateValid(false);
      } else {
        setDiagnosisDateValid(true);
      }
    } else {
      setDiagnosisDateErrorMessage(
        'Please tell us when you received your diagnosis.',
      );
      setDiagnosisDateValid(false);
    }
  }, [monthDiagnosed, yearDiagnosed]);

  useEffect(() => {
    if (yearDmtStarted !== '' && monthDmtStarted !== '') {
      if (
        Number(yearDmtStarted) >= new Date().getFullYear() &&
        Number(monthDmtStarted) > new Date().getMonth()
      ) {
        setDmtStartDateErrorMessage(
          "Please don't select a date in the future.",
        );
        setDmtStartDateValid(false);
      } else {
        setDmtStartDateValid(true);
      }
    } else {
      setDmtStartDateErrorMessage(
        'Please tell us when you started taking your DMT.',
      );
      setDmtStartDateValid(false);
    }
  }, [monthDmtStarted, yearDmtStarted]);

  useEffect(() => {
    if (renderOtherTreatmentField) {
      if (content !== '') {
        setTreatmentValid(true);
      } else {
        setTreatmentValid(false);
      }
    }
  }, [content, renderOtherTreatmentField]);

  useEffect(() => {
    let mounted = true;
    if (
      authLoaded &&
      (!isLoggedIn || !settings?.needsOnboarding) &&
      mounted &&
      uid &&
      auth?.email
    ) {
      eventAdd({
        uid,
        eventType: 'ONBOARDING:COMPLETE',
        eventSource: 'ONBOARDING',
        eventSourceId: uid,
        meta: {},
        email: auth.email,
        platform: 'web',
      });
      router.push('/home');

      return () => {
        mounted = false;
      };
    }
  }, [
    authLoaded,
    isLoggedIn,
    router,
    settings?.needsOnboarding,
    uid,
    auth?.email,
  ]);

  useEffect(() => {
    if (uid && auth?.email) {
      eventAdd({
        uid,
        eventType: 'USER:WELCOME_PAGE',
        eventSource: 'USER',
        eventSourceId: uid,
        meta: {},
        email: auth.email,
        platform: 'web',
      });
    }
  }, [uid, auth?.email]);

  if (!authLoaded) {
    return <PageLoading />;
  }

  return (
    <div className="settings-profile profile container page-container">
      <Head>
        <title>Welcome to Shift.ms</title>
      </Head>
      <div className="container is-narrow">
        <div className="card">
          <div className="card-content">
            <h1 className="title is-size-3 is-size-5-mobile">
              Build your profile
            </h1>
            <div className="onboarding">
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label htmlFor="onboarding-dob-day" className="label">
                    Date of birth
                  </label>
                  <div className="control is-grouped">
                    <div className="select">
                      <select id="onboarding-dob-day" {...bindDobDay}>
                        {getDateOptions()}
                      </select>
                    </div>
                    <div className="select">
                      <select {...bindDobMonth}>{getMonthOptions()}</select>
                    </div>
                    <div className="select">
                      <select {...bindDobYear}>{getYearOptions()}</select>
                    </div>
                  </div>
                  <p className="help">
                    We only show your age on the site, not your exact date of
                    birth.
                  </p>
                  <p className="help">
                    <strong>
                      You need to be 16 years old or above to use our site.
                    </strong>
                  </p>

                  {!dobValid && dobTouched ? (
                    <p className="help is-danger">
                      Please tell us your date of birth.
                    </p>
                  ) : (
                    dobValid && ''
                  )}
                </div>

                <div className="field">
                  <label htmlFor="onboarding-genderOption" className="label">
                    Gender
                  </label>
                  <div className="select">
                    <select
                      id="onboarding-genderOption"
                      name="genderOption"
                      value={genderOption}
                      onBlur={() =>
                        setTouched({ ...touched, genderOption: true })
                      }
                      onChange={event => {
                        setGenderOption(event.target.value);
                        setGenderOptionValid(event.target.value !== '');
                        setShowGenderOtherField(
                          !!(event.target.value === 'Other'),
                        );
                      }}>
                      <option key="gender-unset" value="">
                        ---
                      </option>
                      <option key="gender-male" value="Male">
                        Male
                      </option>
                      <option key="gender-female" value="Female">
                        Female
                      </option>
                      <option key="gender-other" value="Other">
                        Other
                      </option>
                    </select>
                  </div>
                  <p className="help">
                    We don&#39;t show your gender anywhere on the site, but it
                    helps us get a better picture of our users.
                  </p>
                  {!genderOptionValid && touched.genderOption ? (
                    <p className="help is-danger">
                      Please tell us your gender.
                    </p>
                  ) : (
                    genderOptionValid && ''
                  )}
                </div>

                {showGenderOtherField ? (
                  <div className="field">
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        placeholder="Please enter your gender"
                        {...bindGender}
                      />
                    </div>
                  </div>
                ) : (
                  ''
                )}

                <div className="field">
                  <label
                    htmlFor="onboarding-location-country"
                    className="label">
                    Location
                  </label>
                  <div className="select">
                    <select
                      id="onboarding-location-country"
                      onBlur={() => setTouched({ ...touched, country: true })}
                      onChange={(
                        event: React.ChangeEvent<HTMLSelectElement>,
                      ) => {
                        const cIndex = event.target.selectedIndex;
                        const cName = event.target.options[cIndex].textContent;
                        setCountry(event.target.value);
                        setCountryName(cName || '');
                        setCountryValid(countryIsNotEmpty(event.target.value));

                        setRegion('');
                      }}>
                      <option key="country-value-unset" value="">
                        ---
                      </option>
                      <option key="country-value-gb-top" value="GB">
                        United Kingdom
                      </option>
                      <option key="country-value-us-top" value="US">
                        United States
                      </option>
                      {Array.isArray(countriesList) &&
                        countriesList.map(countryOption => (
                          <option
                            key={`country-value-${countryOption.shortCode}`}
                            value={countryOption.shortCode}>
                            {countryOption.countryName}
                          </option>
                        ))}
                    </select>
                  </div>

                  {!countryValid && touched.country ? (
                    <p className="help is-danger">
                      Please tell us where you live.
                    </p>
                  ) : (
                    countryValid && ''
                  )}
                </div>
                {countriesWithRegions.map(
                  reg =>
                    countryName === reg.name && (
                      <div
                        className="field"
                        key={`country-regions-${reg.shortName}`}>
                        <Dropdown
                          options={reg.states}
                          onBlur={() =>
                            setTouched({ ...touched, region: true })
                          }
                          label={
                            reg.shortName === 'UK'
                              ? `Which part of the ${reg.shortName}?`
                              : `Which state?`
                          }
                          onchange={e => {
                            setRegion(e.target.value as Region);
                            setCounty('');
                          }}
                          value={region}
                        />
                        {!isRegionValid && touched.region ? (
                          <p className="help is-danger">
                            Please tell us where you live.
                          </p>
                        ) : (
                          isRegionValid && ''
                        )}
                      </div>
                    ),
                )}
                {countiesOfUK.map(
                  reg =>
                    reg.region === region && (
                      <div
                        className="field"
                        key={`country-regions-${reg.region}`}>
                        <Dropdown
                          options={reg.counties}
                          onBlur={() =>
                            setTouched({ ...touched, county: true })
                          }
                          label={`Which county of ${reg.region}?`}
                          onchange={e => {
                            setCounty(e.target.value);
                          }}
                          value={county}
                        />
                        {!isCountyValid && touched.county ? (
                          <p className="help is-danger">
                            Please tell us where you live.
                          </p>
                        ) : (
                          isCountyValid && ''
                        )}
                      </div>
                    ),
                )}
                <div className="field">
                  <label htmlFor="onboarding-msConnection" className="label">
                    Your connection to MS
                  </label>
                  <div className="select">
                    <select
                      id="onboarding-msConnection"
                      name="msConnection"
                      value={msConnection}
                      onBlur={() =>
                        setTouched({ ...touched, msConnection: true })
                      }
                      onChange={event => {
                        setMsConnection(event.target.value as MSConnection);
                        setMsConnectionValid(event.target.value !== '');
                        if (event.target.value === MSConnection['I have MS']) {
                          setShowMSFields(true);
                          setMsTypeValid(msType !== '');
                          setDiagnosisDateValid(yearDiagnosed !== '');
                          setIsTakingDMTValid(isTakingDMT !== '');
                          setIsOnDesiredDMTValid(isOnDesiredDMT !== '');
                          setDmtStartDateValid(yearDmtStarted !== '');
                          setDmtInfluentialPersonValid(
                            dmtInfluentialPerson !== '',
                          );
                          setDmtRecommendationScoreValid(
                            !!dmtRecommendationScore,
                          );
                          setHcpReferralValid(hcpReferral !== '');
                        } else {
                          setShowMSFields(false);
                          setMsTypeValid(true);
                          setDiagnosisDateValid(true);
                          setReasonForNotUsingDMTValid(true);
                          setIsTakingDMTValid(true);
                          setIsOnDesiredDMTValid(true);
                          setDmtStartDateValid(true);
                          setDmtInfluentialPersonValid(true);
                          setDmtRecommendationScoreValid(true);
                          setTreatmentValid(true);
                          setImportantFactorInDmtDecisionValid(true);
                          setHcpReferralValid(true);
                        }
                      }}>
                      <option key="ms-connections-unset" value="">
                        ---
                      </option>
                      {MSConnections.map(val => (
                        <option key={`ms-connections-${val}`} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!msConnectionValid && touched.msConnection ? (
                    <p className="help is-danger">
                      Please tell us your connection to MS
                    </p>
                  ) : (
                    msConnectionValid && ''
                  )}
                </div>

                {showMSFields ? (
                  <>
                    <div className="notification is-info is-light">
                      <h4>Why are we asking for this information?</h4>
                      <div className="is-size-6 is-font-brand">
                        Sharing your experiences helps MSers learn from each
                        other, and allows Shift.ms to provide a more relevant
                        experience.
                      </div>
                    </div>
                    <div className="field">
                      <label htmlFor="onboarding-msType" className="label">
                        MS type
                      </label>
                      <div className="select">
                        <select
                          id="onboarding-msType"
                          name="msType"
                          onBlur={() =>
                            setTouched({ ...touched, msType: true })
                          }
                          value={msType}
                          onChange={event => {
                            setMsType(event.target.value as MSTypesEnum);
                            setMsTypeValid(event.target.value !== '');
                          }}>
                          <option value="">---</option>
                          {MSTypes.map(val => (
                            <option key={`ms-types-${val}`} value={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                      </div>

                      {!msTypeValid && touched.msType ? (
                        <p className="help is-danger">
                          Please tell us the type of MS you have
                        </p>
                      ) : (
                        msTypeValid && ''
                      )}
                    </div>

                    <div className="field">
                      <label
                        htmlFor="onboarding-yearDiagnosed"
                        className="label">
                        When were you diagnosed?
                      </label>
                      <div className="control is-grouped">
                        <div className="select">
                          <select
                            value={monthDiagnosed}
                            onChange={event => {
                              setMonthDiagnosed(
                                event.target.value as number | '',
                              );
                            }}>
                            {getMonthOptions()}
                          </select>
                        </div>
                        <div className="select">
                          <select
                            id="onboarding-yearDiagnosed"
                            name="yearDiagnosed"
                            value={yearDiagnosed}
                            onBlur={() =>
                              setTouched({ ...touched, diagnosisDate: true })
                            }
                            onChange={event => {
                              setYearDiagnosed(
                                event.target.value as number | '',
                              );
                            }}>
                            <option value="">yyyy</option>
                            {getYearDiagnosedOptions()}
                          </select>
                        </div>
                      </div>
                      {!diagnosisDateValid && touched.diagnosisDate ? (
                        <p className="help is-danger">
                          {diagnosisDateErrorMessage}
                        </p>
                      ) : (
                        diagnosisDateValid && ''
                      )}
                    </div>
                    <div className="field">
                      <label htmlFor="onboarding-dmtUse" className="label">
                        Are you currently taking a disease modifying treatment
                        (DMT) for your MS?
                      </label>
                      <div className="field">
                        <input
                          className="is-checkradio"
                          id="dmtUsageYes"
                          type="radio"
                          value="yes"
                          aria-labelledby="dmtUsageYes"
                          name="dmtUsage"
                          onChange={onDmtUsageChange}
                          checked={isTakingDMT === 'yes'}
                          onBlur={() =>
                            setTouched({ ...touched, isTakingDMT: true })
                          }
                        />
                        <label htmlFor="dmtUsageYes">Yes</label>
                        <input
                          className="is-checkradio"
                          id="dmtUsageNo"
                          type="radio"
                          value="no"
                          name="dmtUsage"
                          aria-labelledby="dmtUsageNo"
                          onChange={onDmtUsageChange}
                          checked={isTakingDMT === 'no'}
                        />
                        <label htmlFor="dmtUsageNo">No</label>
                      </div>
                      {!isTakingDMTValid && touched.isTakingDMT ? (
                        <p className="help is-danger">
                          Please tell us the type of MS you have
                        </p>
                      ) : (
                        isTakingDMTValid && ''
                      )}
                    </div>

                    {isTakingDMT === 'no' && (
                      <div className="field">
                        <label
                          htmlFor="onboarding-reasonForNotUsingDMT"
                          className="label">
                          If no, why are you not on a DMT
                        </label>
                        <div className="select">
                          <select
                            id="onboarding-reasonForNotUsingDMT"
                            name="reasonForNotUsingDMT"
                            value={reasonForNotUsingDMT}
                            onBlur={() =>
                              setTouched({
                                ...touched,
                                reasonForNotUsingDMT: true,
                              })
                            }
                            onChange={event => {
                              setReasonForNotUsingDMT(
                                event.target.value as MSReasonsForNoDmtTypes,
                              );
                              setReasonForNotUsingDMTValid(
                                event.target.value !== '',
                              );
                            }}>
                            <option value="">---</option>
                            {Object.keys(MSReasonsForNoDmt).map(reason => (
                              <option
                                key={`ms-reasonForNotUsingDMT-${reason}`}
                                value={reason}>
                                {
                                  MSReasonsForNoDmt[
                                    reason as keyof MSReasonsforNoDmtOptions
                                  ]
                                }
                              </option>
                            ))}
                          </select>
                        </div>

                        {!reasonForNotUsingDMTValid &&
                        touched.reasonForNotUsingDMT ? (
                          <p className="help is-danger">
                            Please tell us why are you not on DMT
                          </p>
                        ) : (
                          msTypeValid && ''
                        )}
                      </div>
                    )}

                    {isTakingDMT === 'yes' && (
                      <>
                        <div className="field">
                          <label
                            htmlFor="onboarding-treatment"
                            className="label">
                            Which treatment (DMT) are you on?
                          </label>
                          <div className="select">
                            <select
                              id="onboarding-treatment"
                              name="treatment"
                              onBlur={() =>
                                setTouched({ ...touched, treatment: true })
                              }
                              value={treatment}
                              onChange={event => {
                                const { value } = event.target;
                                const isOther =
                                  value.replace(' ', '').split('-')[0] ===
                                  'Other';
                                setOtherTreatmentField(isOther);
                                setTreatmentValid(!isOther);
                                resetContent();
                                setTreatment(value);
                                setTreatmentValid(value !== '');
                              }}>
                              <option value="">---</option>
                              {getTreatmentOptions()}
                            </select>
                          </div>
                          {!renderOtherTreatmentField &&
                          !treatmentValid &&
                          touched.treatment ? (
                            <p className="help is-danger">
                              Please tell us if you are on any treatment
                            </p>
                          ) : (
                            treatmentValid && ''
                          )}
                        </div>
                        {renderOtherTreatmentField && (
                          <div className="field">
                            <label className="label" htmlFor="treatment">
                              Enter your Treatment
                            </label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                {...bindContent}
                                id="treatment"
                              />
                            </div>
                            {!treatmentValid && touched.treatment ? (
                              <p className="help is-danger">
                                What other treatment are you currently taking?
                              </p>
                            ) : (
                              treatmentValid && ''
                            )}
                          </div>
                        )}
                        <div className="field">
                          <label
                            htmlFor="onboarding-yearDMTStarted"
                            className="label">
                            When did you start taking this DMT?
                          </label>
                          <div className="control is-grouped">
                            <div className="select">
                              <select
                                value={monthDmtStarted}
                                onChange={event => {
                                  setMonthDmtStarted(
                                    event.target.value as number | '',
                                  );
                                }}>
                                {getMonthOptions()}
                              </select>
                            </div>
                            <div className="select">
                              <select
                                id="onboarding-yearDMTStarted"
                                name="yearDMTStarted"
                                onBlur={() =>
                                  setTouched({
                                    ...touched,
                                    dmtStartDate: true,
                                  })
                                }
                                value={yearDmtStarted}
                                onChange={event => {
                                  setYearDmtStarted(
                                    event.target.value as number | '',
                                  );
                                }}>
                                <option value="">yyyy</option>
                                {getYearDiagnosedOptions()}
                              </select>
                            </div>
                          </div>
                          {!dmtStartDateValid && touched.dmtStartDate ? (
                            <p className="help is-danger">
                              {dmtStartDateErrorMessage}
                            </p>
                          ) : (
                            dmtStartDateValid && ''
                          )}
                        </div>
                        <div className="field">
                          <label
                            htmlFor="onboarding-dmtYouWantToBeOn"
                            className="label">
                            Are you on the DMT you want to be on?
                          </label>
                          <div className="field">
                            <input
                              className="is-checkradio"
                              id="dmtYouWantToBeOnYes"
                              type="radio"
                              checked={isOnDesiredDMT === 'yes'}
                              value="yes"
                              aria-labelledby="dmtYouWantToBeOnYes"
                              name="dmtYouWantToBeOn"
                              onChange={event => {
                                setIsOnDesiredDMT(
                                  event.target.value as YesNoRadioType,
                                );
                                setIsOnDesiredDMTValid(
                                  event.target.value !== '',
                                );
                              }}
                              onBlur={() =>
                                setTouched({
                                  ...touched,
                                  isOnDesiredDMT: true,
                                })
                              }
                            />
                            <label htmlFor="dmtYouWantToBeOnYes">Yes</label>
                            <input
                              className="is-checkradio"
                              id="dmtYouWantToBeOnNo"
                              type="radio"
                              value="no"
                              checked={isOnDesiredDMT === 'no'}
                              name="dmtYouWantToBeOn"
                              aria-labelledby="dmtYouWantToBeOnNo"
                              onChange={event => {
                                setIsOnDesiredDMT(
                                  event.target.value as YesNoRadioType,
                                );
                                setIsOnDesiredDMTValid(
                                  event.target.value !== '',
                                );
                              }}
                            />
                            <label htmlFor="dmtYouWantToBeOnNo">No</label>
                          </div>
                          {!isOnDesiredDMTValid && touched.isOnDesiredDMT ? (
                            <p className="help is-danger">
                              Please tell us are you on DMT you want to be on?
                            </p>
                          ) : (
                            isOnDesiredDMTValid && ''
                          )}
                        </div>
                      </>
                    )}

                    {isTakingDMT !== '' && (
                      <div className="field">
                        <label
                          htmlFor="onboarding-dmtInfluentialPerson"
                          className="label">
                          Who was most influential when making your DMT
                          decision?
                        </label>
                        <div className="select">
                          <select
                            id="onboarding-dmtInfluentialPerson"
                            name="dmtInfluentialPerson"
                            value={dmtInfluentialPerson}
                            onBlur={() =>
                              setTouched({
                                ...touched,
                                dmtInfluentialPerson: true,
                              })
                            }
                            onChange={event => {
                              setDmtInfluentialPerson(
                                event.target
                                  .value as msWhoInfluencedDmtDecision,
                              );
                              setDmtInfluentialPersonValid(
                                event.target.value !== '',
                              );
                            }}>
                            <option value="">---</option>
                            {MSWhoInfluencedDmtDecisionList.map(val => (
                              <option
                                key={`ms-InfluencedDMTDecision-${val}`}
                                value={val}>
                                {val}
                              </option>
                            ))}
                          </select>
                        </div>

                        {!dmtInfluentialPersonValid &&
                        touched.dmtInfluentialPerson ? (
                          <p className="help is-danger">
                            Please tell us about who influenced you when making
                            your DMT decision
                          </p>
                        ) : (
                          dmtInfluentialPersonValid && ''
                        )}
                      </div>
                    )}

                    {isTakingDMT === 'yes' && (
                      <>
                        <div className="field">
                          <label
                            htmlFor="onboarding-importantFactorInDmtDecision"
                            className="label">
                            What was the most important factor when making your
                            DMT decision?
                          </label>
                          <div className="select">
                            <select
                              id="onboarding-importantFactorInDmtDecision"
                              name="importantFactorInDmtDecision"
                              value={importantFactorInDmtDecision}
                              onBlur={() =>
                                setTouched({
                                  ...touched,
                                  importantFactorInDmtDecision: true,
                                })
                              }
                              onChange={event => {
                                setImportantFactorInDmtDecision(
                                  event.target.value as MSConcernType,
                                );
                                setImportantFactorInDmtDecisionValid(
                                  event.target.value !== '',
                                );
                              }}>
                              <option value="">---</option>
                              {MSImportantFactorsInDmtDecision.map(val => (
                                <option
                                  key={`ms-importantFactorInDmtDecision-${val}`}
                                  value={val}>
                                  {val}
                                </option>
                              ))}
                            </select>
                          </div>

                          {!importantFactorInDmtDecisionValid &&
                          touched.importantFactorInDmtDecision ? (
                            <p className="help is-danger">
                              Please tell us about who influenced you when
                              making your DMT decision
                            </p>
                          ) : (
                            importantFactorInDmtDecisionValid && ''
                          )}
                        </div>

                        <div className="field">
                          <label
                            htmlFor="onboarding-dmtRecommendationScore"
                            className="label">
                            How likely are you to recommend your DMT to other
                            MSers?
                          </label>

                          <Score
                            handleFeedback={(e, score) => {
                              setDmtRecommendationScore(score);
                              setDmtRecommendationScoreValid(true);
                            }}
                            selectedValue={dmtRecommendationScore as number}
                          />

                          {!dmtRecommendationScoreValid &&
                          touched.dmtRecommendationScore ? (
                            <p className="help is-danger">
                              Please tell us about how likely you will recommend
                              your DMT to others MSers.
                            </p>
                          ) : (
                            dmtRecommendationScoreValid && ''
                          )}
                        </div>

                        <div className="field">
                          <RankPreference
                            list={msImportantActivitiesList}
                            updateList={setMsImportantActivitiesList}
                            isMsImportantActivitiesListValid={
                              isMsImportantActivitiesListValid
                            }
                            touched={touched.msImportantActivitiesList}
                          />
                        </div>
                      </>
                    )}

                    <div className="field">
                      <label htmlFor="onboarding-hcpReferral" className="label">
                        Did you hear about Shift.ms from a healthcare
                        professional?
                      </label>
                      <div className="select">
                        <select
                          id="onboarding-hcpReferral"
                          name="hcpReferral"
                          value={hcpReferral}
                          onBlur={() =>
                            setTouched({
                              ...touched,
                              hcpReferral: true,
                            })
                          }
                          onChange={event => {
                            setHcpReferral(
                              event.target.value as YesNoRadioType,
                            );
                            if (event.target.value !== '') {
                              setHcpReferralValid(true);
                            } else {
                              setHcpReferralValid(false);
                            }
                          }}>
                          <option value="">---</option>
                          <option
                            key={`onboarding-hcpReferral-yes`}
                            value="yes">
                            Yes
                          </option>
                          <option key={`onboarding-hcpReferral-no`} value="no">
                            No
                          </option>
                        </select>
                      </div>
                      {!hcpReferralValid && touched.hcpReferral && (
                        <p className="help is-danger">
                          Please tell us whether you heard about Shift.ms from a
                          healthcare professional.
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  ''
                )}
                <div className="control mb-4">
                  <label htmlFor="researchOptInYes" className="label">
                    Interested in hearing about MS research and ways to get
                    involved?
                  </label>
                  <div className="field">
                    <input
                      className="is-checkradio"
                      id="researchOptInYes"
                      type="radio"
                      value="yes"
                      aria-labelledby="researchOptInYes"
                      name="researchOptIn"
                      onChange={handleResearchOptIn}
                      onBlur={() =>
                        setTouched({ ...touched, researchOpt: true })
                      }
                    />
                    <label htmlFor="researchOptInYes">Yes</label>
                    <input
                      className="is-checkradio"
                      id="researchOptInNo"
                      type="radio"
                      value="no"
                      name="researchOptIn"
                      aria-labelledby="researchOptInNo"
                      onChange={handleResearchOptIn}
                    />
                    <label htmlFor="researchOptInNo">No</label>
                  </div>

                  {!researchOptInValid && touched.researchOpt ? (
                    <p className="help is-danger">
                      Please tell us if you&#39;re happy to hear about
                      opportunities
                    </p>
                  ) : (
                    researchOptInValid && ''
                  )}
                </div>

                <div className="control mb-4">
                  <p className="mb-2">
                    We only ask for information about your MS that we feel
                    improves your experience and helps the community better
                    support each other.
                  </p>

                  <div className="field">
                    <input
                      className="is-checkradio"
                      id="dataPermission"
                      type="checkbox"
                      value="yes"
                      aria-labelledby="dataPermission"
                      name="dataPermission"
                      onBlur={() =>
                        setTouched({ ...touched, dataPermission: true })
                      }
                      onChange={handleDataPermission}
                    />
                    <label htmlFor="dataPermission" className="label">
                      I confirm that I am happy to share the information in this
                      form with Shift.ms
                    </label>

                    {!dataPermission && touched.dataPermission ? (
                      <p className="help is-danger">This field is compulsory</p>
                    ) : (
                      dataPermission && ''
                    )}
                  </div>
                </div>

                <div className="field submit-field">
                  <div className="control">
                    {dobValid &&
                    genderOptionValid &&
                    countryValid &&
                    msConnectionValid &&
                    msTypeValid &&
                    treatmentValid &&
                    diagnosisDateValid &&
                    researchOptInValid &&
                    dataPermission &&
                    isTakingDMTValid &&
                    dmtStartDateValid &&
                    isOnDesiredDMTValid &&
                    dmtInfluentialPersonValid &&
                    importantFactorInDmtDecisionValid &&
                    reasonForNotUsingDMTValid &&
                    dmtRecommendationScoreValid &&
                    isMsImportantActivitiesListValid &&
                    isRegionValid &&
                    isCountyValid &&
                    hcpReferralValid ? (
                      <button
                        type="submit"
                        className={`button is-rounded is-medium is-fullwidth is-primary${
                          isSubmitted ? ' is-loading' : ''
                        }`}
                        disabled={false}>
                        Save
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="button is-rounded is-medium is-fullwidth is-primary"
                        disabled>
                        Save
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
